import React, { forwardRef, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardActions,
    Slide,
    Dialog,
    AppBar,
    Toolbar,
    IconButton,
    Stack,
    Tooltip,
    Collapse,
    Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Markdown from 'react-markdown';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import ChatInput from './chat/ChatInput';
import { Title } from '@mui/icons-material';
import UserNeedScores from './UserNeedScores';
import Link from '@mui/material/Link';
import InfoIcon from './InfoIcon';
import DetailedUserNeedScores from './DetailedUserNeedScoring';

function ExternalLink({ url, ariaLabel = 'Open external link' }) {
    return (
        <Link href={url} target="_blank" rel="noopener noreferrer">
            <IconButton aria-label={ariaLabel} size="small">
                <OpenInNewIcon />
            </IconButton>
        </Link>
    );
}

const DEBUG = false;

const FEATURE_SINGLE_CONTENT_CHAT = process.env.REACT_APP_FEATURE_SINGLE_CONTENT_CHAT === "true"
const FEATURE_ANALYSIS_METADATA = process.env.REACT_APP_FEATURE_ANALYSIS_METADATA === "true"

export const MainUserNeed = ({ metadata }) => {
    if (DEBUG) console.log("MainUserNeed :: render", metadata);

    if (!FEATURE_ANALYSIS_METADATA || !metadata) {
        return null;
    }

    const userNeed = typeof metadata.user_need === "string" ?
        JSON.parse(metadata.user_need)
        : metadata.user_need;

    if (!userNeed || !userNeed.drive) {
        return null;
    }

    if (DEBUG) console.log("MainUserNeed :: drive", userNeed.drive);

    return <span>{userNeed.drive}</span>;
}

const ArticleMetadata = ({
    article
}) => {
    if (!FEATURE_ANALYSIS_METADATA || !article || !article.metadata) {
        return null;
    }

    const metadata = article.metadata;
    const userNeed = typeof metadata.user_need === "string" ?
        JSON.parse(metadata.user_need)
        : metadata.user_need;
    const userNeedScoring = typeof userNeed.scoring === "string" ?
        JSON.parse(userNeed.scoring)
        : userNeed.scoring;
    const userNeedDetailedScoring = typeof userNeed.detailed_scoring === "string" ?
        JSON.parse(userNeed.detailed_scoring)
        : userNeed.detailed_scoring;
    const toneAnalysis = typeof metadata.tone?.yleissävy === "string" ?
        JSON.parse(metadata.tone?.yleissävy)
        : metadata.tone?.yleissävy;

    const FIELDS = [
        "theme",
        "topics",
        "people",
        "organizations",
        "country",
        "city",
        "neighborhood",
        "locations",
    ];
    const metaFieldsExist = FIELDS.find(field => !!metadata[field]);

    if (DEBUG) console.log("ArticleMetadata :: render :: userNeed", metadata, userNeed, userNeedScoring, userNeedDetailedScoring);

    const MetaField = ({ field }) => {
        if (!metadata[field]) {
            return null;
        }

        const metaFieldValue = (metadata[field]) instanceof Array ?
            metadata[field].join(", ")
            : metadata[field];

        return <Typography variant="body2">
            <FormattedMessage id={`article.metadata.${field}`} />: {metaFieldValue}
        </Typography>
    }

    return (
        <Stack direction="column" spacing={5}>
            {
                metaFieldsExist &&
                <Stack direction="column" spacing={1}>
                    <Typography variant="h5">
                        <FormattedMessage id="article.metadata.entities" /> <InfoIcon id="info.article.ai.metadata" />
                    </Typography>
                    <br />
                    {
                        FIELDS.map((field, index) => {
                            return <MetaField
                                key={index}
                                field={field} />;
                        })
                    }
                </Stack>
            }
            {
                toneAnalysis &&
                <Stack direction="column" spacing={2}>
                    <Typography variant="h5">
                        <FormattedMessage id="article.tone.title" />  <InfoIcon id="info.article.ai.metadata" />
                    </Typography>
                    <Typography variant="body2">
                        <FormattedMessage id="article.tone.main" />: {
                            toneAnalysis.tone instanceof Array ?
                                //toneAnalysis.tone.map(item => `"${item}"`).join(", ")
                                <span>"{toneAnalysis.tone[0]}"</span>
                                : <span>"{toneAnalysis.tone}"</span>
                        }
                    </Typography>
                    <Typography variant="body">
                        {toneAnalysis.analysis}
                    </Typography>
                </Stack>
            }
            <Stack direction="column" spacing={2}>
                <Typography variant="h5">
                    <FormattedMessage id="article.userneed.title" />  <InfoIcon id="info.article.ai.metadata" />
                </Typography>
                <Typography variant="body2">
                    <FormattedMessage id="article.userneed.drive" />: "<MainUserNeed metadata={metadata} />"
                </Typography>
                {
                    false && //Removing this for a while as it's missing in the data
                    <Typography variant="body2">
                        <FormattedMessage id="article.userneed.needs" />: {userNeed.need.map(item => `"${item}"`).join(", ")}
                    </Typography>
                }
                <Typography variant="body">
                    {userNeed.analysis}
                </Typography>
                <br />
                <Typography variant="h5">
                    <FormattedMessage
                        id="article.userneed.scoring.title" />  <InfoIcon id="info.article.userneed" />
                </Typography>
                {
                    userNeedDetailedScoring ?
                        <DetailedUserNeedScores scores={userNeedDetailedScoring} />
                        : <UserNeedScores scores={userNeedScoring} />
                }

            </Stack>
        </Stack>
    )
}

const Article = ({
    brand,
    date,
    title,
    score,
    summary,
    sub,
    actions,
    openAction,
    sx }) => {
    if (DEBUG) console.log("Article :: render", brand, date, title, score, summary, sub, actions);

    const intl = useIntl();

    return (
        <Box
            sx={{
                minWidth: 275,
                textAlign: 'left',
                ...sx
            }}>
            <Card variant="outlined">
                <CardContent>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        width="100%">
                        {
                            (brand || date) &&
                            <Typography
                                variant="body"
                                sx={{
                                    textAlign: 'left',
                                    color: 'text.secondary',
                                    fontSize: 14
                                }}>
                                {brand} <FormattedDate value={date} />
                            </Typography>
                        }
                        {
                            score &&
                            <Typography
                                variant="body2"
                                sx={{
                                    textAlign: 'right',
                                    color: 'text.secondary',
                                    fontSize: 14
                                }}>
                                <FormattedMessage id="search.relevance" />: {Math.round(score * 100)} %
                            </Typography>
                        }
                    </Stack>
                    {
                        title &&
                        <Typography variant="subtitle2" component="div">
                            <Markdown>{title}</Markdown>
                        </Typography>
                    }
                    {
                        summary &&
                        <Typography variant="body2">
                            {summary}
                        </Typography>
                    }
                </CardContent>
                {
                    (actions || sub) &&
                    <CardActions>
                        <Stack
                            width="100%"
                            direction="row"
                            justifyContent="space-between">
                            {
                                sub &&
                                <Typography
                                    variant="body2"
                                    sx={{
                                        textAlign: 'right',
                                        color: 'text.secondary',
                                        fontSize: 14,
                                        marginLeft: "0.5em"
                                    }}>
                                    {sub}
                                </Typography>
                            }
                            <span></span>
                            <Button
                                size="small"
                                onClick={
                                    () => {
                                        openAction && openAction()
                                    }}
                            >
                                <FormattedMessage id="search.result.show" />
                            </Button>
                        </Stack>
                    </CardActions>
                }
            </Card>
        </Box >
    );
}


const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export function LandingPageArticle({
    article,
}) {

}

export function FullScreenArticle({
    article,
    open,
    setOpen,
    sendAction,
    sendMutation }) {
    const [expanded, setExpanded] = useState(false);
    const [text, setText] = useState('');
    const intl = useIntl();
    const tooltipTitle = intl.formatMessage({ id: "article.link.tooltip" });

    const handleClose = () => {
        setOpen(false);
    };

    if (!article) {
        return null;
    }

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            key={article.id}
        >
            <AppBar sx={{ position: 'relative' }}>
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography
                        sx={{ ml: 2, flex: 1 }}
                        variant="h6"
                        component="div">
                        {article.brand}
                    </Typography>
                </Toolbar>
            </AppBar>
            <Box
                mx="10%"
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                <Stack
                    direction={"column"}
                    spacing={3}
                    p={3}
                    maxWidth={850}>
                    <Typography variant="h4" component="div">
                        <Markdown>
                            {article.title}
                        </Markdown>
                    </Typography>

                    <Typography variant="subtitle2" component="div">
                        {article.brand} {article.domain && `[${article.domain}]`}, <FormattedDate value={article.update_date} /> {article.url && <Tooltip title={tooltipTitle}><ExternalLink url={article.url} ariaLabel={tooltipTitle} /></Tooltip>}
                        <br />
                        {article.authors_writers.join(", ")}
                    </Typography>

                    <Card>
                        <CardContent>
                            <Typography variant="h5">
                                <FormattedMessage id="article.summary" />
                            </Typography>
                            <Typography variant="body1" component="div">
                                <Markdown>
                                    {article.summary}
                                </Markdown>
                            </Typography>
                        </CardContent>
                    </Card>

                    <Button onClick={() => setExpanded(prev => !prev)}>
                        {expanded ? <FormattedMessage id="article.hidedisplay.button" /> :
                            <FormattedMessage id="article.fulldisplay.button" />}
                    </Button>

                    <Collapse in={expanded}>
                        <Paper elevation={2} sx={{ p: 2, mt: 0 }}>
                            <Typography variant="body1" component="div">
                                <Markdown>
                                    {article.body}
                                </Markdown>
                            </Typography>
                        </Paper>
                    </Collapse>

                    <Typography variant="h5" component="div">
                        <FormattedMessage id="article.tags" /> <InfoIcon id="info.article.categories" />
                    </Typography>
                    <Typography variant="body2" component="div">
                        {
                            article.tags.join(" | ")
                                .replaceAll(",", " | ")
                        }
                    </Typography>
                    <br />
                    <ArticleMetadata
                        article={article} />
                    <br />
                    {
                        FEATURE_SINGLE_CONTENT_CHAT &&
                        <>
                            <Typography variant="h5" component="div">
                                <FormattedMessage id="article.discussion.start" />
                            </Typography>
                            <ChatInput
                                disabled={sendMutation.isPending}
                                text={text}
                                setText={setText}
                                placeholder={intl.formatMessage({ id: "chat.input.start" })}
                                send={(message) => {
                                    sendAction && sendAction(message, [article.id]);
                                }}
                                sending={sendMutation.isPending}
                                info={<FormattedMessage id="info.article.chat" />}
                            />
                            <br />
                        </>
                    }
                </Stack>
            </Box>
        </Dialog>
    );
}

export default Article;